import type { UserOperation } from 'viem/account-abstraction';

type UserOperationWithGasFields = Pick<
  UserOperation,
  | 'callGasLimit'
  | 'verificationGasLimit'
  | 'preVerificationGas'
  | 'maxFeePerGas'
>;

/**
 * Type guard to check if a partial user operation has all required gas fields.
 *
 * This function narrows the type of a Partial<UserOperation> to ensure it has
 * the required gas fields needed for fee calculation. Use this when you need to
 * validate that gas fields are present before performing fee calculations.
 *
 * @param userOperation - The partial user operation to check.
 * @returns True if all required gas fields are present, false otherwise.
 *
 * @example
 * ```typescript
 * if (hasRequiredGasFields(userOperation)) {
 *   // TypeScript now knows these fields are defined
 *   const fee = calculateFee({
 *     callGasLimit: userOperation.callGasLimit,
 *     maxFeePerGas: userOperation.maxFeePerGas,
 *     preVerificationGas: userOperation.preVerificationGas,
 *     verificationGasLimit: userOperation.verificationGasLimit,
 *   });
 * }
 * ```
 */
export const hasRequiredGasFields = (
  userOperation: Partial<UserOperation>
): userOperation is Partial<UserOperation> & UserOperationWithGasFields =>
  Boolean(
    userOperation.callGasLimit &&
      userOperation.verificationGasLimit &&
      userOperation.preVerificationGas &&
      userOperation.maxFeePerGas
  );
